import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import Loading from 'App/components/loading';
import { ACCOUNT_SUPPORT_MOBILE_SIDE_NAV_OPEN, MENU_ROUTES } from 'App/components/sidebar/constants';
import AccountSidebar from 'Pages/help/components/accountSidebar';
import { HELP_PAGE_TEST_ID } from 'Pages/help/constants';
import {
  AccountContainer,
  AccountContentContainer,
  AccountSidebarContainer,
  LoggedOutHeaderGap,
  MobileNavbarContainer,
  MobileNavbarTitle, SnackBarContainer
} from 'Pages/help/style';
import { SnackbarProvider } from 'Shared/components/design/snackbar/SnackbarProvider';
import {ICON_IDS} from "Shared/components/icons/constants";
import Icon from 'Shared/components/icons/icon';
import { isInMobileApp } from 'Shared/services/mobileAppMessenger';

type UserActivationProps = {
  isLoggedIn : boolean
}

/**
 * Renders the User Activation component
 */
function UserActivation({ isLoggedIn }: UserActivationProps): JSX.Element {
  const { t }                                   = useTranslation('accountHelp');
  const location                                = useLocation();
  const navigateTo                              = useNavigate();
  const params                                  = useParams();
  const isMobileAccountViewActive               = location.search !== ACCOUNT_SUPPORT_MOBILE_SIDE_NAV_OPEN;
  const [showMobileNavbar, setShowMobileNavbar] = useState<boolean>(isMobileAccountViewActive);

  /**
   * Listen for URL changes so the right component is shown in mobile view
   */
  useEffect(() => {
    setShowMobileNavbar(window.location.search !== ACCOUNT_SUPPORT_MOBILE_SIDE_NAV_OPEN);
  },[isMobileAccountViewActive, location]);

  const getMobileNavbarTitle = () => {
    if (location.pathname.startsWith(MENU_ROUTES.ACCOUNT_PROFILE_QUESTIONS)) {
      return t('AccountSidebar--menuItem-profileQuestions');
    }

    switch(location.pathname) {
      case MENU_ROUTES.ACCOUNT_PERSONAL_INFO:
        return t('AccountSidebar--menuItem-personalInfo');
      case MENU_ROUTES.ACCOUNT_PERSONAL_INFO_EDIT_NAME:
        return t('EditName--header-title');
      case MENU_ROUTES.ACCOUNT_PERSONAL_INFO_EDIT_CODE:
        return t('EditPostCode--header-title');
      case MENU_ROUTES.ACCOUNT_PERMISSIONS:
        return t('AccountSidebar--menuItem-permissions');
      case MENU_ROUTES.ACCOUNT_PROFILE_QUESTIONS:
        return t('AccountSidebar--menuItem-profileQuestions');
      case MENU_ROUTES.ACCOUNT_PROFILE:
        return t('AccountSidebar--menuItem-userProfile');
      case MENU_ROUTES.ACCOUNT_CONTACT_US:
        return t('AccountSidebar--menuItem-contactUs');
      case MENU_ROUTES.ACCOUNT_SUPPORT:
        return t('AccountSidebar--menuItem-support');
      case MENU_ROUTES.ACCOUNT_INVITE_TERMS:
          return t('AccountSidebar--menuItem-inviteTerms');
      case MENU_ROUTES.ACCOUNT_TERMS:
        return t('AccountSidebar--menuItem-terms');
      case MENU_ROUTES.ACCOUNT_PRIVACY:
        return t('AccountSidebar--menuItem-privacy');
      case MENU_ROUTES.ACCOUNT_INVITE:
        return t('AccountSidebar--inviteFriends');
      case MENU_ROUTES.ACCOUNT_LEADERBOARD:
        return t('AccountSidebar--menuItem-leaderboard');
      default:
        return '';
    }
  };

  const onMobileNavbarBack = () => {
    if (location.pathname.startsWith(MENU_ROUTES.ACCOUNT_PROFILE_QUESTIONS) && params?.id) {
      navigateTo(MENU_ROUTES.ACCOUNT_PROFILE_QUESTIONS);
    }

    switch (location.pathname) {
      case MENU_ROUTES.ACCOUNT_PERSONAL_INFO_EDIT_NAME:
      case MENU_ROUTES.ACCOUNT_PERSONAL_INFO_EDIT_CODE:
        navigateTo(MENU_ROUTES.ACCOUNT_PERSONAL_INFO);
        return;
      case MENU_ROUTES.ACCOUNT_PROFILE_QUESTIONS:
      case MENU_ROUTES.ACCOUNT_PERSONAL_INFO:
      case MENU_ROUTES.ACCOUNT_SUPPORT:
      case MENU_ROUTES.ACCOUNT_CONTACT_US:
      case MENU_ROUTES.ACCOUNT_TERMS:
      case MENU_ROUTES.ACCOUNT_PRIVACY:
        navigateTo(MENU_ROUTES.ACCOUNT_SUPPORT_MOBILE_SIDE_NAV_OPEN);
        return;
      default:
        setShowMobileNavbar(false);
    }
  };

  /**
   * Renders the mobile nav bar component also hides it if the user is in the mobile app
   * @returns JSX.Element
   */
  const renderMobileNavbar = (): JSX.Element | undefined => {
    if (!showMobileNavbar || isInMobileApp()) {
      return;
    }

    const navbarTitle = getMobileNavbarTitle();

    return <MobileNavbarContainer>
      <Icon iconId={ICON_IDS.ARROW_LEFT} onClick={() => onMobileNavbarBack()} />
      <MobileNavbarTitle>{ navbarTitle }</MobileNavbarTitle>
    </MobileNavbarContainer>;
  };

  return (
    <AccountContainer data-testid={HELP_PAGE_TEST_ID}>
      <AccountSidebarContainer isVisible={ !showMobileNavbar } isLoggedIn={ isLoggedIn }>
        <AccountSidebar />
      </AccountSidebarContainer>
      <AccountContentContainer isVisible={ showMobileNavbar }>
        { renderMobileNavbar() }
        {
          !isLoggedIn && <LoggedOutHeaderGap />
        }
        <SnackbarProvider justifyContent="flex-end" Container={SnackBarContainer}>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </SnackbarProvider>
      </AccountContentContainer>
    </AccountContainer>
  );
}

export default UserActivation;
